import { annotation } from '@cornerstonejs/tools';
import { Internal, OHIF } from '@gleamer/types';
import { Accordion, labelPass, useTask } from '@gleamer/ui';
import { CheckBadgeIcon } from '@heroicons/react/24/outline';
import { EyeIcon, EyeSlashIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useViewportGrid } from '@ohif/ui-next';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { isValidObservation } from '../../../../../utils/validation.utils';
import { ObservationCharacterisation } from './ObservationCharacterisation';
import { RegionList } from './RegionList';
import ObservationFalseNegative from './ObservationFalseNegative';

const { deleteObservation, deleteRegion, toggleObservationVisibility } =
  labelPass.actions;

type ServicesManager = OHIF.ServicesManager;
type ObservationSubmission = Internal.ObservationInternal;

type ObservationTableProps = {
  servicesManager: ServicesManager;
  observation: ObservationSubmission;
  index: number;
  open?: boolean;
  onObservationClick: () => void;
};

function useSyncWithOpen(open: boolean) {
  const [currentlyOpened, setCurrentlyOpened] = useState<boolean>(open);

  useEffect(() => {
    setCurrentlyOpened(open);
  }, [open]);

  const toggle = useCallback(
    () => setCurrentlyOpened(wasOpened => !wasOpened),
    []
  );

  return [currentlyOpened, toggle] as const;
}

export default function ObservationItem({
  servicesManager,
  observation,
  index,
  open = false,
  onObservationClick,
}: ObservationTableProps) {
  const {
    measurementService: MeasurementService,
    cornerstoneViewportService: CornerstoneViewportService,
    viewportGridService,
    displaySetService,
  } = servicesManager.services;

  // @ts-ignore: Bad OHIF typing
  const [viewportGrid] = useViewportGrid();
  const { activeViewportId, viewports } = viewportGrid;

  const { t } = useTranslation();
  const task = useTask();
  const dispatch = useDispatch();

  const [openRegions, toggleRegions] = useSyncWithOpen(open);

  // Task is loaded asynchronously, wait for it to be retrieved
  if (!task?.observations) {
    return null;
  }

  const obsSpec = task.observations.find(
    obs => obs.code === observation.code && obs.status === observation.status
  );

  if (!obsSpec) {
    return null;
  }

  const { label, colors } = obsSpec;
  const color = colors[index % colors.length];

  const isValid = isValidObservation(task, observation);

  const jumpToMeasurement = (viewportIndex, regionUID) => {
    MeasurementService.jumpToMeasurement(viewportIndex, regionUID);
    //If we don't need to jump instances then the region won't be selected
    annotation.selection.setAnnotationSelected(regionUID);
  };

  const onRegionHover = ({ region }) => {
    // setAnnotationSelected(uid) selects with preserveSelected=false, which
    // internally clears the whole selection set (via the null-guarded
    // clearSelectionSet) before selecting. The previous manual deselect loop was
    // redundant and, in CS3D 2.0, crashed: deselectAnnotation() sets
    // `annotation.isSelected` without the null guard its siblings have, so a
    // stale selected UID whose annotation no longer exists throws.
    annotation.selection.setAnnotationSelected(region.uid);
  };

  const onFocusRegion = ({ region }) => {
    const measurement = MeasurementService.getMeasurement(region.uid);
    const referencedDisplaySetInstanceUID = measurement.displaySetInstanceUID;

    // Get all viewports that already contains the region
    const matchingViewportsIndexes: {
      viewportId: string;
      matches: boolean | undefined;
    }[] = [];

    for (const [viewportId, viewport] of viewports.entries()) {
      matchingViewportsIndexes.push({
        viewportId,
        matches: viewport.displaySetInstanceUIDs?.includes(
          referencedDisplaySetInstanceUID
        ),
      });
    }

    // If no viewport contains the region, display its series in the active
    // viewport. OHIF 3.10's `handleJumpToMeasurement` only navigates *within* the
    // current series (`isReferenceViewable` bails otherwise); the cross-series
    // displaySet switch used to be handled by `@ohif/extension-measurement-tracking`,
    // which this app doesn't use — so the region's series must be loaded explicitly,
    // landing on the region's slice (matching `TrackedMeasurementsContext`).
    if (matchingViewportsIndexes.every(({ matches }) => !matches)) {
      const referencedImageId =
        measurement.referencedImageId ?? region.metadata?.referencedImageId;
      const displaySet = displaySetService.getDisplaySetByUID(
        referencedDisplaySetInstanceUID
      );
      const imageIndex =
        displaySet?.images?.findIndex(
          image => image.imageId === referencedImageId
        ) ?? -1;

      viewportGridService.setDisplaySetsForViewport({
        viewportId: activeViewportId,
        displaySetInstanceUIDs: [referencedDisplaySetInstanceUID],
        viewportOptions: {
          initialImageOptions: { index: imageIndex > 0 ? imageIndex : 0 },
        },
      });
      jumpToMeasurement(activeViewportId, region.uid);
      return;
    }

    // Find all viewports that contains the region.
    const exactMatches = matchingViewportsIndexes
      .filter(match => match.matches)
      .map(({ viewportId }) => {
        const viewport =
          CornerstoneViewportService.getCornerstoneViewport(viewportId);

        if (!viewport) {
          console.log(`Viewport ${viewportId} is not exact`);
          return {
            viewportId,
            isExact: false,
          };
        }

        return {
          viewportId,
          isExact:
            viewport.getCurrentImageIdIndex() ===
            viewport
              .getImageIds()
              .findIndex(uid => region.metadata.referencedImageId === uid),
        };
      });

    // If at least one viewport is already displaying the region,
    // use the first viewport index that displays it
    const firstMatchingExactly = exactMatches.find(({ isExact }) => isExact);
    if (firstMatchingExactly) {
      jumpToMeasurement(firstMatchingExactly.viewportId, region.uid);
      return;
    }

    // If the active viewport contains the region, use the active viewport
    if (
      matchingViewportsIndexes.some(
        match => match.viewportId === activeViewportId && match.matches
      )
    ) {
      jumpToMeasurement(activeViewportId, region.uid);
      return;
    }

    // Use the first viewport that contain the region
    jumpToMeasurement(
      matchingViewportsIndexes.find(({ matches }) => matches).viewportId,
      region.uid
    );
  };

  const onRegionMouseLeave = ({ region }) => {
    // Deleting a region unmounts its row, which fires mouse-leave for a region
    // whose annotation is already gone. CS3D's deselectAnnotation sets
    // `annotation.isSelected = false` WITHOUT the null guard that
    // selectAnnotation/clearSelectionSet have, so that throws
    // "Cannot set properties of undefined (setting 'isSelected')".
    if (!annotation.state.getAnnotationManager().getAnnotation(region.uid)) {
      return;
    }
    annotation.selection.deselectAnnotation(region.uid);
  };

  const onDeleteRegion = ({ region }) => {
    dispatch(deleteRegion(region.uid));
  };

  const onDeleteObservation = () => {
    dispatch(deleteObservation(observation.uid));
  };

  const onToggleObservationVisibility = () => {
    dispatch(toggleObservationVisibility(observation.uid));
  };

  const regionCount = observation.regions?.length ?? 0;
  const iconButton =
    'inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg text-dl-muted hover:bg-dl-sunken hover:text-dl-text focus:outline-none focus-visible:ring-2 focus-visible:ring-dl-accent';

  // the design's lesion card: a raised card in the lesion's colour — a 4px stripe on the left, the whole border once
  // open, a tint on its header (the 2026-10-27 proposal) — its name, how many regions, and quiet actions
  return (
    <div
      className="mx-3 my-2 overflow-hidden rounded-xl border border-l-4 bg-dl-raised"
      data-testid="observation-card"
      style={{ borderColor: open ? color : 'rgb(var(--dl-border))', borderLeftColor: color }}
    >
      <Accordion open={open} onClick={onObservationClick}>
        <div
          className="z-9 sticky top-0 flex items-center gap-2.5 border-b py-2.5 pl-3.5 pr-2 text-dl-text"
          style={{
            backgroundColor: `color-mix(in srgb, ${color} 18%, rgb(var(--dl-raised)))`,
            borderBottomColor: `color-mix(in srgb, ${color} 35%, rgb(var(--dl-border)))`,
          }}
        >
          {/* one line for the name and its status, the region count under it; no capitals nor letter-spacing, so a
              long name ("Uncertain lesion 1") is cut, not wrapped mid-word */}
          <Accordion.Title className="min-w-0 flex-initial text-left normal-case !tracking-normal">
            <span className="flex min-w-0 flex-col items-start leading-tight">
              <h4 className="flex max-w-full items-center gap-[5px] whitespace-nowrap text-[13px] font-semibold">
                <span className="truncate">
                  {label} {index}
                </span>
                {isValid && (
                  <CheckBadgeIcon aria-label={t('Complete')} className="h-3.5 w-3.5 shrink-0 text-dl-success" />
                )}
              </h4>
              <span className="mt-px text-[11px] font-normal text-dl-faint">
                {regionCount === 1 ? t('1 region') : t('{{count}} regions', { count: regionCount })}
              </span>
            </span>
          </Accordion.Title>
          <ObservationFalseNegative
            confidence={observation?.confidence}
            status={observation?.status}
            falseNegativeThreshold={task.falseNegativeThreshold}
          />
          <span className="flex-1" />
          <button
            type="button"
            className={iconButton}
            title={t('Hide observation regions')}
            aria-label={t('Hide observation regions')}
            onClick={onToggleObservationVisibility}
          >
            {observation.hidden ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          </button>
          <button type="button" className={iconButton} title={t('Delete')} aria-label={t('Delete')} onClick={onDeleteObservation}>
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      <Accordion.Content>
        <div className="invisible-scrollbar overflow-y-auto overflow-x-hidden px-3.5 py-3">
          <ObservationCharacterisation
            observation={observation}
            servicesManager={servicesManager}
          />
          <hr className="-mx-3.5 my-3 border-dl-border" />
          <Accordion open={openRegions} onClick={toggleRegions}>
            <div className="flex justify-between py-1 text-dl-text">
              <Accordion.Title>
                <h5 className="text-[13px] font-semibold">{t('Regions')}</h5>
              </Accordion.Title>
            </div>
            <Accordion.Content>
              <div className="invisible-scrollbar overflow-y-auto overflow-x-hidden">
                <RegionList
                  observation={observation}
                  onFocusRegion={onFocusRegion}
                  onRegionHover={onRegionHover}
                  onRegionMouseLeave={onRegionMouseLeave}
                  onDelete={onDeleteRegion}
                  open={open}
                  servicesManager={servicesManager}
                />
              </div>
            </Accordion.Content>
          </Accordion>
        </div>
      </Accordion.Content>
      </Accordion>
    </div>
  );
}
